import { formatDisplayLabel } from './camelCaseLabel';
import { formatLeaveDuration } from './dateTime';
import { isValidLeaveDate } from './leaveFilters';
import type {
  LeaveBalance,
  LeaveDurationOption,
  LeaveDurationRule,
  LeaveSessionOption,
} from '../types/leave';

export function normalizeSessionCode(value: string | null | undefined): string | null {
  if (!value?.trim()) return null;
  return value.trim().toUpperCase().replace(/[\s-]+/g, '_');
}

export function groupLeaveDurationOptions(
  sessions: LeaveSessionOption[],
): LeaveDurationOption[] {
  const byCode = new Map<string, LeaveDurationOption>();
  for (const session of sessions) {
    const durationCode = normalizeSessionCode(session.durationCode);
    if (!durationCode) continue;
    const existing = byCode.get(durationCode);
    if (existing) {
      existing.sessions.push(session);
      continue;
    }
    byCode.set(durationCode, {
      durationCode,
      durationName: session.durationName,
      durationDays: session.durationDays,
      isHalfDay: session.isHalfDay,
      requiresExplicitRule: session.requiresExplicitRule === true,
      displayOrder: session.displayOrder,
      sessions: [session],
    });
  }
  return [...byCode.values()]
    .map(option => ({
      ...option,
      sessions: [...option.sessions].sort(
        (left, right) => left.displayOrder - right.displayOrder,
      ),
    }))
    .sort((left, right) => left.displayOrder - right.displayOrder);
}

export function findLeaveDurationRule(
  rules: LeaveDurationRule[] | null | undefined,
  leaveTypeId: number | null | undefined,
  durationCode: string | null | undefined,
): LeaveDurationRule | null {
  const code = normalizeSessionCode(durationCode);
  if (!rules?.length || leaveTypeId == null || !code) return null;
  return (
    rules.find(
      rule => rule.leaveTypeId === leaveTypeId && rule.durationCode === code,
    ) ?? null
  );
}

export function durationsAllowedForBalance(
  durations: LeaveDurationOption[],
  balance: LeaveBalance | null,
  rules: LeaveDurationRule[] = [],
): LeaveDurationOption[] {
  return durations.filter(option => {
    if (!balance) return false;
    const rule = findLeaveDurationRule(
      rules,
      balance.leaveTypeId,
      option.durationCode,
    );
    if (rule) return rule.isAllowed;
    if (option.requiresExplicitRule) return false;
    if (option.isHalfDay) {
      return balance.allowHalfDay !== false;
    }
    return balance.allowFullDay !== false;
  });
}

/** Whole calendar days from today until the leave date (0 = same day). */
export function leaveNoticeDays(today: string, fromDate: string): number | null {
  if (!isValidLeaveDate(today) || !isValidLeaveDate(fromDate)) return null;
  const todayMs = Date.parse(`${today}T00:00:00Z`);
  const fromMs = Date.parse(`${fromDate}T00:00:00Z`);
  if (Number.isNaN(todayMs) || Number.isNaN(fromMs)) return null;
  return Math.round((fromMs - todayMs) / 86400000);
}

export function localTodayDate(now: Date = new Date()): string {
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

function pluralDays(value: number): string {
  return `${value} day${value === 1 ? '' : 's'}`;
}

/** Short policy hint for the selected leave type and duration, from database rules. */
export function leaveDurationRuleHint(
  rule: LeaveDurationRule | null,
): string | null {
  if (!rule || !rule.isAllowed) return null;
  const parts: string[] = [];
  parts.push(
    rule.minimumAdvanceDays > 0
      ? `Apply at least ${pluralDays(rule.minimumAdvanceDays)} before the leave date.`
      : 'Can be applied any time.',
  );
  if (rule.maximumRequestsPerMonth != null) {
    parts.push(
      `Up to ${rule.maximumRequestsPerMonth} request${
        rule.maximumRequestsPerMonth === 1 ? '' : 's'
      } per month.`,
    );
  }
  return parts.join(' ');
}

function sameLeaveType(
  application: { leaveTypeId?: number | null; leaveTypeName: string | null },
  rule: LeaveDurationRule,
): boolean {
  if (application.leaveTypeId != null) {
    return application.leaveTypeId === rule.leaveTypeId;
  }
  const name = application.leaveTypeName?.trim().toLowerCase();
  return !!name && name === rule.leaveTypeName?.trim().toLowerCase();
}

/**
 * Advance-notice and monthly request limits from the database rule. The server
 * re-validates both; this only gives earlier feedback.
 */
export function leaveDurationRuleViolation(input: {
  rule: LeaveDurationRule | null;
  durationName: string;
  leaveTypeName: string;
  fromDate: string;
  today: string;
  sessions: LeaveSessionOption[];
  monthApplications: Array<{
    leaveTypeId?: number | null;
    leaveTypeName: string | null;
    leaveFromDate: string | null;
    sessionType: string | null;
    statusCode: string | null;
  }>;
}): string | null {
  const { rule } = input;
  if (!rule) return null;
  if (!rule.isAllowed) {
    return `${input.durationName} is not allowed for ${input.leaveTypeName}.`;
  }
  if (rule.minimumAdvanceDays > 0) {
    const notice = leaveNoticeDays(input.today, input.fromDate);
    if (notice != null && notice < rule.minimumAdvanceDays) {
      return `${input.durationName} ${input.leaveTypeName} must be applied at least ${pluralDays(
        rule.minimumAdvanceDays,
      )} before the leave date.`;
    }
  }
  if (rule.maximumRequestsPerMonth != null) {
    const month = input.fromDate.slice(0, 7);
    const used = input.monthApplications.filter(application => {
      if (!isBlockingLeaveApplicationStatus(application.statusCode)) return false;
      if (application.leaveFromDate?.slice(0, 7) !== month) return false;
      if (!sameLeaveType(application, rule)) return false;
      const session = findSessionOption(input.sessions, application.sessionType);
      return normalizeSessionCode(session?.durationCode) === rule.durationCode;
    }).length;
    if (used >= rule.maximumRequestsPerMonth) {
      return `Only ${rule.maximumRequestsPerMonth} ${input.durationName} request${
        rule.maximumRequestsPerMonth === 1 ? '' : 's'
      } per month are allowed for ${input.leaveTypeName}.`;
    }
  }
  return null;
}

/**
 * Balance the employee can use now: non-carry-forward policies are limited to
 * the current month's credit, computed by the server.
 */
export function usableLeaveBalance(balance: LeaveBalance | null): number | null {
  if (!balance) return null;
  if (
    balance.carryForwardAllowed === false &&
    balance.monthAvailableBalance != null &&
    Number.isFinite(balance.monthAvailableBalance)
  ) {
    return balance.monthAvailableBalance;
  }
  return balance.available;
}

/**
 * Partial-day duration shorter than half of the longest configured duration
 * (Quarter Day under a catalog that also has Full Day / Half Day). The
 * comparison uses duration days from the session catalog, not a fixed hour count.
 */
export function isQuarterDayDuration(
  duration: LeaveDurationOption | null | undefined,
  durations: LeaveDurationOption[],
): boolean {
  if (!duration?.isHalfDay || !(duration.durationDays > 0)) return false;
  const fullDays = durations.reduce(
    (max, option) => Math.max(max, option.durationDays),
    0,
  );
  if (!(fullDays > 0)) return false;
  return duration.durationDays + 1e-9 < fullDays / 2;
}

/**
 * "Quarter" when the stored day count is a partial share shorter than half a
 * leave day (the catalog quarter duration). Half-day (0.5) and full-day stay
 * "Session".
 */
export function leavePortionLabelForDays(
  totalDays: number | null | undefined,
): string {
  if (typeof totalDays !== 'number' || !Number.isFinite(totalDays)) return 'Session';
  if (totalDays > 0 && totalDays + 1e-9 < 0.5) return 'Quarter';
  return 'Session';
}

/** "Quarter" for a quarter-day session from the catalog; "Session" otherwise. */
export function leavePortionFieldLabel(
  sessionType: string | null | undefined,
  sessions: LeaveSessionOption[] = [],
): string {
  const match = findSessionOption(sessions, sessionType);
  if (!match) return 'Session';
  const durations = groupLeaveDurationOptions(sessions);
  const duration =
    durations.find(option => option.durationCode === match.durationCode) ?? null;
  return isQuarterDayDuration(duration, durations) ? 'Quarter' : 'Session';
}

export function findSessionOption(
  sessions: LeaveSessionOption[],
  sessionCode: string | null | undefined,
): LeaveSessionOption | null {
  const code = normalizeSessionCode(sessionCode);
  if (!code) return null;
  return sessions.find(session => session.sessionCode === code) ?? null;
}

export function formatLeaveSessionLabel(
  sessionType: string | null | undefined,
  sessions: LeaveSessionOption[] = [],
  sessionTypeName?: string | null,
): string {
  const named = sessionTypeName?.trim();
  if (named) return formatDisplayLabel(named);
  const match = findSessionOption(sessions, sessionType);
  if (match?.sessionName) return formatDisplayLabel(match.sessionName);
  return 'Not available';
}

export function computeInclusiveLeaveDays(
  fromDate: string,
  toDate: string,
): number | null {
  const dates = listInclusiveLeaveDates(fromDate, toDate);
  return dates == null ? null : dates.length;
}

/** Calendar dates from fromDate through toDate, inclusive. */
export function listInclusiveLeaveDates(
  fromDate: string,
  toDate: string,
): string[] | null {
  if (!isValidLeaveDate(fromDate) || !isValidLeaveDate(toDate)) return null;
  if (fromDate > toDate) return null;
  const fromMs = Date.parse(`${fromDate}T00:00:00Z`);
  const toMs = Date.parse(`${toDate}T00:00:00Z`);
  if (Number.isNaN(fromMs) || Number.isNaN(toMs)) return null;
  const dates: string[] = [];
  for (let ms = fromMs; ms <= toMs; ms += 86400000) {
    dates.push(new Date(ms).toISOString().slice(0, 10));
  }
  return dates;
}

function asOffDateSet(
  offDates: Iterable<string> | null | undefined,
): Set<string> {
  return offDates instanceof Set ? offDates : new Set(offDates ?? []);
}

/** Working days in the range after Office Calendar holidays and weekly offs. */
export function computeChargeableLeaveDays(
  fromDate: string,
  toDate: string,
  offDates?: Iterable<string> | null,
): number | null {
  const dates = listInclusiveLeaveDates(fromDate, toDate);
  if (dates == null) return null;
  const off = asOffDateSet(offDates);
  return dates.filter(date => !off.has(date)).length;
}

export function computeLeaveTotalDays(
  fromDate: string,
  toDate: string,
  option: LeaveDurationOption | LeaveSessionOption | null,
  offDates?: Iterable<string> | null,
): number | null {
  if (!option) return null;
  const off = asOffDateSet(offDates);
  if (option.isHalfDay) {
    if (!isValidLeaveDate(fromDate)) return null;
    if (toDate && toDate !== fromDate) return null;
    if (off.has(fromDate)) return 0;
    return option.durationDays;
  }
  const days = computeChargeableLeaveDays(fromDate, toDate, off);
  if (days == null) return null;
  return days * option.durationDays;
}

const BLOCKING_LEAVE_STATUSES = new Set(['PENDING', 'APPROVED']);

export function isBlockingLeaveApplicationStatus(
  statusCode: string | null | undefined,
): boolean {
  return BLOCKING_LEAVE_STATUSES.has((statusCode || '').toUpperCase());
}

function applicationCoversDate(
  fromDate: string | null | undefined,
  toDate: string | null | undefined,
  date: string,
): boolean {
  if (!isValidLeaveDate(date)) return false;
  const start = fromDate?.trim() ?? '';
  const end = (toDate?.trim() || fromDate?.trim()) ?? '';
  if (!isValidLeaveDate(start) || !isValidLeaveDate(end)) return false;
  return start <= date && end >= date;
}

/**
 * Same-day leave rules from stored applications and the session catalog:
 * complementary half-day sessions may share a date; a full day, both halves,
 * or a duplicate session may not.
 */
export function conflictingLeaveApplicationMessage(input: {
  applications: Array<{
    leaveFromDate: string | null;
    leaveToDate: string | null;
    sessionType: string | null;
    statusCode: string | null;
  }>;
  sessions: LeaveSessionOption[];
  fromDate: string;
  toDate: string;
  requested: LeaveSessionOption;
}): string | null {
  const dates = listInclusiveLeaveDates(input.fromDate, input.toDate);
  if (dates == null || dates.length === 0) return null;
  const requestedCode = normalizeSessionCode(input.requested.sessionCode);
  if (!requestedCode) return null;
  const requestedDays = input.requested.durationDays;
  if (!Number.isFinite(requestedDays) || requestedDays <= 0) return null;

  for (const date of dates) {
    const covering = input.applications.filter(
      application =>
        isBlockingLeaveApplicationStatus(application.statusCode) &&
        applicationCoversDate(
          application.leaveFromDate,
          application.leaveToDate,
          date,
        ),
    );
    let usedDays = 0;
    for (const application of covering) {
      const existingCode = normalizeSessionCode(application.sessionType);
      if (existingCode && existingCode === requestedCode) {
        return `${input.requested.sessionName} leave is already applied for the selected date.`;
      }
      const existing = findSessionOption(input.sessions, application.sessionType);
      if (!existing) {
        return 'A leave application already exists for the selected date.';
      }
      if (!existing.isHalfDay) {
        return `${existing.sessionName} leave is already applied for the selected date.`;
      }
      usedDays += existing.durationDays;
    }
    if (!input.requested.isHalfDay && covering.length > 0) {
      return 'A leave application already exists for the selected date.';
    }
    if (usedDays + requestedDays > 1 + 1e-9) {
      return 'Leave is already applied for the full day on the selected date.';
    }
  }
  return null;
}

export function hasSufficientLeaveBalance(
  available: number | null | undefined,
  requestedDays: number,
): boolean {
  if (!Number.isFinite(requestedDays) || requestedDays <= 0) return false;
  if (available == null || !Number.isFinite(available)) return true;
  return available + 1e-9 >= requestedDays;
}

export function formatLeaveBalanceValue(
  available: number | null | undefined,
  unit = 'DAYS',
): string {
  if (available == null || !Number.isFinite(available)) return 'Not available';
  return formatLeaveDuration(available, unit);
}

export function formatLeaveApplicationDuration(
  totalDays: number | null | undefined,
  sessionType?: string | null,
  sessions: LeaveSessionOption[] = [],
  sessionTypeName?: string | null,
): string {
  const duration = formatLeaveDuration(totalDays);
  const session = formatLeaveSessionLabel(sessionType, sessions, sessionTypeName);
  const match = findSessionOption(sessions, sessionType);
  if (!match || !match.isHalfDay) return duration;
  if (duration === 'Not available') return session;
  return `${duration} (${session})`;
}
